import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  TextField,
  Paper,
  TableContainer,
  TableFooter,
} from '@mui/material';
import { useQuery } from 'react-query';
import { format, toZonedTime } from 'date-fns-tz';
import recoveryServerDbManager from '../../../services/RecoveryServerDbManager';
import { useDebounce } from '../../../hooks/useDebounce';
import { getDbInfoFromRecoveryServer, loadDbFromRecoveryServerWithFilename } from '../../../db/datamanager';
import { alertWarn } from '../../../alertDispatcher';
import DatabasePreview from './DatabasePreview';

const INDIANAPOLIS_TZ = 'America/Indiana/Indianapolis';
const PAGE_SIZE = 10;

type FileEntry = {
  name: string;
  timestamp: number;
  type: string;
};

export type PreviewData = {
  missionName: string;
  boxes: {
    uid: string;
    closed: boolean;
    samplesNumber: number;
  }[];
} | null;

export const parseMcDatabaseFilename = (filename: string): FileEntry => {
  const [timestampStr, rest] = filename.split('-', 2);
  const parsedTimestamp = parseInt(timestampStr, 10);

  if (!parsedTimestamp || !rest) {
    return {
      name: filename,
      timestamp: 0,
      type: 'unknown',
    };
  }

  const type = rest.split('.')[0];

  return {
    name: filename,
    timestamp: parsedTimestamp,
    type,
  };
};

const formatDate = (ms: number): string => {
  const date = new Date(ms);
  const zoned = toZonedTime(date, INDIANAPOLIS_TZ);

  return format(zoned, 'yyyy-MM-dd HH:mm:ss zzz', { timeZone: INDIANAPOLIS_TZ });
};

const DatabasesList: React.FC = () => {
  const [page, setPage] = useState(1); // page is 1-based
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [showDatabasePreview, setShowDatabasePreview] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData>(null);

  const { data, isLoading, error } = useQuery(
    ['files', page, debouncedSearch],
    () => recoveryServerDbManager.listAvailableDbs(page, PAGE_SIZE, debouncedSearch),
    {
      keepPreviousData: true,
    },
  );

  const handlePreview = async (filename: string) => {
    const content = await getDbInfoFromRecoveryServer(filename);

    if (!content) {
      alertWarn('Fetching database content failed');

      return;
    }

    let parsed: Record<string, [any]> | null = null;

    try {
      parsed = JSON.parse(content);
    } catch (e) {
      alertWarn('Failed to parse database content');

      return;
    }

    if (!parsed) {
      alertWarn('No data found in database');

      return;
    }

    const sessions = parsed.Session;
    const session = Array.isArray(sessions) && sessions.length > 0 ? sessions[0] : null;

    if (!session) {
      alertWarn('No session data found in database');

      return;
    }

    const missionInstanceId = session.Mission_id;
    const mission = parsed.Mission?.find((m) => m._instance_id === missionInstanceId);

    const boxesInfo = (parsed.SampleBox || [])
      .map((box) => ({
        uid: box.uid,
        closed: Boolean(box.closedTimestamp),
        samplesNumber: box._refs?.Sample?.length || 0,
      }))
      .sort((a, b) => {
        if (a.closed && !b.closed) {
          return 1;
        }

        if (!a.closed && b.closed) {
          return -1;
        }

        return a.uid.localeCompare(b.uid);
      });

    setPreviewData({ missionName: mission?.name || 'No current mission', boxes: boxesInfo });
    setShowDatabasePreview(true);
  };

  const handleLoad = async (filename: string) => {
    const result = await loadDbFromRecoveryServerWithFilename(filename);

    if (!result) {
      alertWarn('Loading from recovery server failed');
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error loading files.</div>;
  }

  if (!data) {
    return <div>No files found.</div>;
  }

  const filesParsed = data.mc_databases_list.map((file) => parseMcDatabaseFilename(file));

  return (
    <>
      <TextField
        label="Search"
        variant="outlined"
        size="small"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1); // Reset to first page on search
        }}
        style={{ marginBottom: 16 }}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filesParsed.map((file) => (
              <TableRow key={file.name}>
                <TableCell>{file.timestamp ? formatDate(file.timestamp) : file.name}</TableCell>
                <TableCell>{file.type}</TableCell>
                <TableCell>
                  <Box display="flex" justifyContent="space-between">
                    <Button variant="contained" color="secondary" onClick={() => handlePreview(file.name)}>
                      Preview
                    </Button>

                    <Button variant="contained" color="primary" onClick={() => handleLoad(file.name)}>
                      Load
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                count={data?.total ?? 0}
                rowsPerPage={PAGE_SIZE}
                page={page - 1} // MUI is 0-based
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                onRowsPerPageChange={() => {}}
                rowsPerPageOptions={[PAGE_SIZE]}
                backIconButtonProps={{ disabled: page === 1 }}
                nextIconButtonProps={{
                  disabled: (data?.page ?? 1) * PAGE_SIZE >= (data?.total ?? 0),
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <DatabasePreview open={showDatabasePreview} onClose={() => setShowDatabasePreview(false)} data={previewData} />
    </>
  );
};

export default DatabasesList;
